import { CONFIG } from "../appConfig";
import request from "../utils/app/request";
import { batchActions } from "../utils/redux";
import { getFormattedMessages } from "../utils/app/messages";
import { getQsFromObj } from "../utils/js/object";
import { Timeout } from "../utils/app/sessionTimeout";
import { AXIOS_TIMEOUT_ERROR_CODE, REQUEST } from "../constants/index";
import { types } from "../utils/js/types";
import { ERRORS } from "../constants/errors";
import services from "../services";

/**
 * This is to avoid multiple /status calls
 */
let requestController = null;

/**
 * @private
 * @description Action creator for request starting
 * @param {Object} data {serviceType:String, fetchingKey:String, clearMessagesOnRequest:Boolean, statePath:Array, clearStateOnRequest:Boolean}
 * @returns {Object}
 */
function onRequestStart(data) {
  const action = {
    type: REQUEST.REQUEST_START,
    serviceType: data.serviceType,
    fetchingKey: data.fetchingKey,
    clearMessagesOnRequest: data.clearMessagesOnRequest,
    cancelFetching: data.cancelFetching,
  };
  if (Array.isArray(data.statePath))
    Object.assign(action, {
      statePath: data.statePath,
      clearStateOnRequest: data.clearStateOnRequest,
    });
  return action;
}

/**
 * @private
 * @description Action creator for request error
 * @param {object} data - Data object
 * @param {string} data.reqUrl - Url string
 * @param {object} data.payload - Payload object
 * @param {string} data.serviceType - Service type
 * @param {Array<object>} data.messages - Messages array
 * @returns {Object}
 */
function onRequestError(data) {
  return {
    type: REQUEST.REQUEST_ERROR,
    reqUrl: data.reqUrl,
    payload: data.payload,
    serviceType: data.serviceType,
    fetchingKey: data.fetchingKey,
    messages: data.messages,
  };
}

/**
 * @private
 * @description Action creator for request success
 * @param {Object} data {serviceType:String, resp:*, statePath:Array}
 * @returns {Object}
 */
function onRequestSuccess(data) {
  if (data.trimRespProp && data.resp[data.trimRespProp])
    data.resp = data.resp[data.trimRespProp];
  return {
    type: REQUEST.REQUEST_SUCCESS,
    serviceType: data.serviceType,
    resp: data.resp,
    statePath: data.statePath,
  };
}

/**
 * @private
 * @description Action creator for request complete
 * @param {Object} data {serviceType:String, fetchingKey:String}
 * @returns {Object}
 */
function onRequestComplete(data) {
  return {
    type: REQUEST.REQUEST_COMPLETE,
    serviceType: data.serviceType,
    fetchingKey: data.fetchingKey,
  };
}

/**
 * @private
 * @description Action creator for updating the GMA or CDA session in redux store
 * @param {Object} session
 * @param {String} serviceType
 * @returns {Object}
 */

function updateSession(session, serviceType) {
  return { type: REQUEST.UPDATE_SESSION, session, serviceType };
}

/**
 * @private
 * @description Action creator to notify when session timed out
 * @param {number} remainingSessionSeconds - Remaining session time in seconds
 * @returns {Object}
 */
const onSessionTimedOut = (remainingSessionSeconds) => {
  return {
    type: REQUEST.SESSION_TIMED_OUT,
    payload: remainingSessionSeconds,
  };
};

/**
 * This is the format used by GMI service configurations for the `request` action (see {@link actions.request}).
 * @typedef {object} CommitPostData
 * @prop {object} payload data to POST to the server, for the request service
 * @prop {object} params the query string parameters for the request service
 * @see actions.reservations  {@link actions.reservations.commit}
 * @memberof actions
 */

/**
 * @name request
 * @description Gma/Solr request action wrapper
 * @param {Object} config `{data, opts, service}`, where `data` is {@link actions.CommitPostData}
 * @returns {Function}
 * @memberof actions
 * @example
 * let cancel = null;
 * const data = {...}; // *(Object)*: data passed to service; data is in format {params:object, payload:object}
 * const opts = {
 *  cancelFn: c => cancel = c, // *(Function)*: Optional. 'cancel' should be a value stored outside the opts
 *  clearMessagesOnRequest: true, // *(Boolean)*: defaults to `true`
 *  clearStateOnRequest: false, // *(Boolean)*: defaults to `false`
 *  headers: {}, // *(Object)*: Optional. Object of custom headers for the request
 *  updateSessionOnResp: true, // *(Boolean)*: defaults to `true`
 *  fullResponse: false, // *(Boolean)*: defaults to `false`
 *  addFetchingKeyInResponse: false // *(Boolean)*: Adds the "fetchingKey" in the response of the request. Defaults to `false`.
 * };
 * const service = services.gma.reservations.carClassCode; // *(Function)*: service function for request
 * // if you want to cancel an existing request
 * if (cancel !== null) {
 *  cancel();
 *  cancel = null;
 * }
 * dispatch(actions.request({data, opts, service}))
 *  // checking resp.cancelled is only necessary if you use cancelFn option
 *  // if a request was cancelled; resp.cancelled === true
 *  .then(resp => {
 *    if (!resp.cancelled) resp.messages ? error() : success();
 *    cancel = null;
 *  });
 *
 * // services noted as fetching via...
 * // state.gmi.fetching.gma (boolean)
 * // state.gmi.fetching.gma_services (array of active requests)
 * // state.gmi.fetching.solr (boolean)
 * // state.gmi.fetching.solr_services (array of active requests)
 */
export default function (config) {
  return (dispatch) => {
    const serviceConfig = config.service(config.data);
    let serviceType;
    if (
      serviceConfig.origin === CONFIG.GMA_ORIGIN_URL ||
      serviceConfig.origin === CONFIG.DYNAMIC_CONTENT_ORIGIN_URL
    ) {
      serviceType = REQUEST.GMA;
    } else if (serviceConfig.origin === CONFIG.CDA_ORIGIN_URL) {
      serviceType = REQUEST.CDA;
    } else if (serviceConfig.origin === CONFIG.SOLR_ORIGIN_URL) {
      serviceType = REQUEST.SOLR;
    }

    const { opts = {} } = config;
    const {
      clearMessagesOnRequest = true,
      clearStateOnRequest = false,
      updateSessionOnResp = true,
      addFetchingKeyInResponse = false,
      cancelFetching,
      allowWarnPriorityErrors = false,
      timeoutConfig,
    } = opts;
    const fetchingKey =
      serviceConfig.endpoint +
      (serviceConfig.params ? getQsFromObj(serviceConfig.params) : "");

    dispatch(
      onRequestStart({
        serviceType,
        fetchingKey,
        clearMessagesOnRequest,
        statePath: serviceConfig.statePath,
        clearStateOnRequest,
        cancelFetching,
      })
    );
    if (opts.cancelFn)
      Object.assign(serviceConfig, { cancelFn: opts.cancelFn });
    if (opts.headers) Object.assign(serviceConfig, { headers: opts.headers });
    if (opts.fullResponse)
      Object.assign(serviceConfig, { fullResponse: opts.fullResponse });
    if (opts.timeoutConfig)
      Object.assign(serviceConfig, { timeoutConfig: opts.timeoutConfig });

    return request(serviceConfig)
      .then(
        (resp) => {
          const response = resp; // save potential reference to fullResponse
          resp = opts.fullResponse ? resp.data : resp;
          const actions = [];
          let hasWarnPriority = false;

          if (serviceType === REQUEST.GMA || serviceType === REQUEST.CDA) {
            // session should update independent of error messages
            const session = resp.session
              ? resp.session
              : resp.gbo || resp.deem
              ? resp
              : null;
            if (session !== null && updateSessionOnResp)
              actions.push(updateSession(session, serviceType));
            // filter out any silent and determine if there is a blocking error
            if (resp.messages && resp.messages.length > 0) {
              resp.messages = resp.messages.reduce((next, message) => {
                const { display_as, priority } = message;
                if (display_as !== "silent") {
                  if (
                    (display_as === "STATUS_I18N_CODE" &&
                      CONFIG.STATUS_I18N_CODE) ||
                    message.message === ""
                  ) {
                    message.message =
                      CONFIG.ERROR_STATUS[message.code] || CONFIG.ERROR_DEFAULT;
                  }

                  if (priority === "WARN") {
                    hasWarnPriority = true;
                  }

                  next.push(message);
                }
                return next;
              }, []);
            }
          }
          if (resp.messages && resp.messages.length > 0) {
            const reqUrl = serviceConfig.origin + fetchingKey;

            if (hasWarnPriority && allowWarnPriorityErrors) {
              actions.push(
                onRequestSuccess({
                  serviceType,
                  resp,
                  statePath: serviceConfig.statePath,
                  trimRespProp: serviceConfig.trimRespProp,
                })
              );
            } else {
              actions.push(
                onRequestError({
                  serviceType,
                  reqUrl,
                  payload: serviceConfig.data,
                  fetchingKey,
                  messages: resp.messages,
                })
              );
            }
          } else if (Array.isArray(serviceConfig.statePath)) {
            actions.push(
              onRequestSuccess({
                serviceType,
                resp,
                statePath: serviceConfig.statePath,
                trimRespProp: serviceConfig.trimRespProp,
              })
            );
          }
          if (actions.length > 1) {
            dispatch(batchActions(...actions));
          } else if (actions.length === 1) {
            dispatch(actions[0]);
          }
          return response;
        },
        (err) => {
          const { cancelled, axiosErrorCode } = err;
          const messages = getFormattedMessages(
            err.message,
            err.exception,
            axiosErrorCode,
            fetchingKey
          );

          // if axios throws a timeout error and we provided a callback, then we call this callback
          if (
            axiosErrorCode === AXIOS_TIMEOUT_ERROR_CODE &&
            timeoutConfig?.onRequestTimeoutCallback
          ) {
            return timeoutConfig?.onRequestTimeoutCallback();
          }

          /**
           * Check AXIOS error code
           * if the error is ERR_NETWORK OR ECCONABORTED and have HEALTH_CHECK_URL configured, we gonna call a /status endpoint
           * if /status returns 200 we gonna return SPOTTY_CONNECTION_ERR_NETWORK code
           * if /status returns error we gonna return CUSTOM OFFLINE code
           */
          if (
            !CONFIG.ERROR_HANDLING_AVOID_STATUS_CALL &&
            !!CONFIG.HEALTH_CHECK_URL &&
            (axiosErrorCode === ERRORS.ERR_NETWORK ||
              axiosErrorCode === AXIOS_TIMEOUT_ERROR_CODE)
          ) {
            /**
             * Check if already have called a /status before
             * If yes we gonna abort the next /status call
             * If no we gonna call /status
             */
            if (requestController) {
              requestController.abort();
            }

            requestController = new AbortController();

            // Step 1: Call GMA /status endpoint
            request({
              ...services.gma.status.status(),
              signal: requestController.signal,
            })
              .then(() => {
                // Step 2: Check if response is OK and return spotty connection
                // SPOTTY_CONNECTION_ERR_NETWORK
                let customErrorMessage = "";

                customErrorMessage = ERRORS.SPOTTY_CONNECTION_ERR_NETWORK;
                requestController = null;

                const errMessages = getFormattedMessages(
                  err.message,
                  err.exception,
                  customErrorMessage,
                  fetchingKey
                );
                dispatch(
                  onRequestError({
                    serviceType,
                    messages: errMessages,
                    fetchingKey,
                  })
                );
              })
              .catch((error) => {
                let customErrorMessage = "";
                // If the request was aborted we not gonna proceed
                if (error?.cancelled) return;

                // Step 3: Call AEM /buildinfo endpoint as fallback
                request({
                  method: "HEAD",
                  origin: CONFIG.HEALTH_CHECK_URL,
                  endpoint: "",
                })
                  .then(() => {
                    // Step 4: Determine error type based on /buildinfo response
                    // GMA_UNREACHABLE_ERR_NETWORK
                    customErrorMessage = ERRORS.GMA_UNREACHABLE_ERR_NETWORK;

                    const errMessages = getFormattedMessages(
                      err.message,
                      err.exception,
                      customErrorMessage,
                      fetchingKey
                    );
                    dispatch(
                      onRequestError({
                        serviceType,
                        messages: errMessages,
                        fetchingKey,
                      })
                    );
                  })
                  .catch(() => {
                    // OFFLINE
                    // TODO: ECR-32162:: dispatch offline event to the client
                  });
              });
          } else if (!cancelled) {
            dispatch(onRequestError({ serviceType, messages, fetchingKey }));
          }

          return { cancelled, messages, axiosErrorCode };
        }
      )
      .then((resp) => {
        dispatch(onRequestComplete({ serviceType, fetchingKey }));

        if (serviceType === REQUEST.GMA) {
          dispatch(handleTimeoutResponse(resp));
        }

        if (addFetchingKeyInResponse) {
          Object.assign(resp, { fetchingKey });
        }

        return resp;
      });
  };
}

/**
 * @private
 * @type {function(Function):void} // (had to use this format for jsdoc2md)
 * @description Handle the timeout response
 * @param {Response} resp -- response from the request
 * @returns {void} returns function that dispatches the `onSessionTimeOut`
 */
export function handleTimeoutResponse(resp) {
  return (dispatch) => {
    let sessionTimeout = getTimeoutValue(resp);

    if (types(sessionTimeout).isDefined) {
      sessionTimeout =
        sessionTimeout && sessionTimeout > 0 ? sessionTimeout : 0;
      Timeout.init(sessionTimeout, (remainingTime) =>
        dispatch(onSessionTimedOut(remainingTime))
      );
    }
  };
}

/**
 * @private
 * @description Get the timeout value from the response
 * @param {Response} resp -- response from the request
 * @returns {number|undefined} sessionTimeout
 */
function getTimeoutValue(resp) {
  const { gma, session, data } = resp;

  if (types(gma?.timeout).isDefined) {
    return gma.timeout;
  }

  if (types(session?.gma?.timeout).isDefined) {
    return session.gma.timeout;
  }

  // inflight modify location search (city search) initiate endpoint response
  if (types(session?.session_timeout).isDefined) {
    return session.session_timeout;
  }

  // reservations/commit we get this format back
  if (types(data?.session).isDefined) {
    return data.session.session_timeout;
  }
  if (types(resp.session_timeout).isDefined) {
    return resp.session_timeout;
  }

  return;
}

/**
 * @typedef {object} Response
 * @prop {GMA} [gma] -- GMA object
 * @prop {Session} [session] -- Session object
 * @prop {number} [session_timeout] -- session timeout value
 */
/**
 * @typedef {object} GMA
 * @prop {number} timeout -- timeout value
 * @prop {NonLoyaltyUnsubscribeIndividual} [non_loyalty_unsubscribe_individual] - Non Loyalty user data
 */
/**
 * @typedef {object} Session
 * @prop {GMA} gma -- GMA object
 * @prop {number} timeout -- timeout value
 */

/**
 * @typedef {object} NonLoyaltyUnsubscribeIndividual
 * @prop {NonLoyaltyIndividual} individual
 */
/**
 * @typedef {object} NonLoyaltyIndividual
 * @prop {object} consents
 * @prop {string} mask_email
 * @prop {object} subscriptions
 * @prop {string} uuid
 */
